import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const {
    language,
    changeLanguage,
    t,
    getAllLanguages,
    getCurrentLanguageInfo
  } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation structure – 100% basée sur des clés i18n
  const navigation = [
    { id: 'home', labelKey: 'nav.home', href: '/' },
    { id: 'about', labelKey: 'nav.about', href: '/uber-uns' },
    { id: 'triax', labelKey: 'nav.triax', href: '/triax' },
    {
      id: 'services',
      labelKey: 'nav.services',
      href: '/services',
      dropdown: [
        {
          id: 'services-change',
          labelKey: 'navDropdown.services.change.title',
          descriptionKey: 'navDropdown.services.change.description',
          href: '/services#service-0'
        },
        {
          id: 'services-bridges',
          labelKey: 'navDropdown.services.bridges.title',
          descriptionKey: 'navDropdown.services.bridges.description',
          href: '/services#service-1'
        },
        {
          id: 'services-scrum4sales',
          labelKey: 'navDropdown.services.scrum4sales.title',
          descriptionKey: 'navDropdown.services.scrum4sales.description',
          href: '/services#service-2'
        },
        {
          id: 'services-leadership',
          labelKey: 'navDropdown.services.leadership.title',
          descriptionKey: 'navDropdown.services.leadership.description',
          href: '/services#service-3'
        }
      ]
    },
    {
      id: 'signature',
      labelKey: 'nav.signature',
      href: '/signature',
      dropdown: [
        {
          id: 'signature-foundation',
          labelKey: 'navDropdown.signature.foundation.title',
          descriptionKey: 'navDropdown.signature.foundation.description',
          href: '/signature#resonance-foundation'
        },
        {
          id: 'signature-accelerator',
          labelKey: 'navDropdown.signature.accelerator.title',
          descriptionKey: 'navDropdown.signature.accelerator.description',
          href: '/signature#resonance-accelerator'
        },
        {
          id: 'signature-transformation',
          labelKey: 'navDropdown.signature.transformation.title',
          descriptionKey: 'navDropdown.signature.transformation.description',
          href: '/signature#resonance-transformation'
        },
        {
          id: 'signature-sos',
          labelKey: 'navDropdown.signature.sos.title',
          descriptionKey: 'navDropdown.signature.sos.description',
          href: '/signature#resonance-sos'
        }
      ]
    },
    { id: 'sos', labelKey: 'nav.sos', href: '/sos' },
    {
      id: 'insights',
      labelKey: 'nav.insights',
      href: '/insights',
      dropdown: [
        {
          id: 'insights-successStories',
          labelKey: 'navDropdown.insights.successStories.title',
          descriptionKey: 'navDropdown.insights.successStories.description',
          href: '/insights/erfolgsgeschichten'
        },
        {
          id: 'insights-caseStudies',
          labelKey: 'navDropdown.insights.caseStudies.title',
          descriptionKey: 'navDropdown.insights.caseStudies.description',
          href: '/insights/case-studies'
        },
        {
          id: 'insights-blog',
          labelKey: 'navDropdown.insights.blog.title',
          descriptionKey: 'navDropdown.insights.blog.description',
          href: '/insights/blog'
        }
      ]
    },
    { id: 'contact', labelKey: 'nav.contact', href: '/kontakt' }
  ];

  const languages = getAllLanguages();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleDropdownEnter = (dropdownId) => {
    setActiveDropdown(dropdownId);
  };

  const handleDropdownLeave = () => {
    setTimeout(() => {
      setActiveDropdown(null);
    }, 100);
  };

  const handleDropdownClick = (dropdownId) => {
    if (activeDropdown === dropdownId) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdownId);
    }
  };

  const handleClickOutside = (e) => {
    if (!e.target.closest('.dropdown-container')) {
      setActiveDropdown(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg border-b border-slate-200/20 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="text-2xl font-light">
              <span className="gradient-text font-medium">GLOB</span>
              <span className={isScrolled ? 'text-slate-900' : 'text-white'}>
                RIDGE
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navigation.map((item) => (
              <div key={item.id} className="relative dropdown-container">
                {item.dropdown ? (
                  <div
                    onMouseEnter={() => handleDropdownEnter(item.id)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    {/* Trigger */}
                    {item.href ? (
                      <Link
                        to={item.href}
                        className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer group ${
                          isActive(item.href)
                            ? 'text-purple-600'
                            : isScrolled
                            ? 'text-slate-700 hover:text-purple-600'
                            : 'text-white/90 hover:text-white'
                        }`}
                        onClick={() => setActiveDropdown(null)}
                      >
                        <span>{t(item.labelKey)}</span>
                        <ChevronDown
                          className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                            activeDropdown === item.id ? 'rotate-180' : ''
                          }`}
                        />
                      </Link>
                    ) : (
                      <div
                        className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer group ${
                          isActive(item.href)
                            ? 'text-purple-600'
                            : isScrolled
                            ? 'text-slate-700 hover:text-purple-600'
                            : 'text-white/90 hover:text-white'
                        }`}
                        onClick={() => handleDropdownClick(item.id)}
                      >
                        <span>{t(item.labelKey)}</span>
                        <ChevronDown
                          className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                            activeDropdown === item.id ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                    )}

                    {/* Dropdown Menu */}
                    {activeDropdown === item.id && (
                      <div className="absolute top-full left-0 mt-1 w-80 bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-slate-200/20 py-4 z-[60]">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.id}
                            to={subItem.href}
                            className="block px-6 py-3 text-slate-700 hover:bg-purple-50 hover:text-purple-600 transition-colors duration-200 group"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <div className="font-medium text-sm">
                              {t(subItem.labelKey)}
                            </div>
                            <div className="text-xs text-slate-500 mt-1 group-hover:text-purple-500">
                              {t(subItem.descriptionKey)}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 group ${
                      isActive(item.href)
                        ? 'text-purple-600'
                        : isScrolled
                        ? 'text-slate-700 hover:text-purple-600'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    <span>{t(item.labelKey)}</span>
                  </Link>
                )}
              </div>
            ))}

            {/* Language Selector */}
            <div className="relative ml-4 dropdown-container">
              <div
                onMouseEnter={() => handleDropdownEnter('language')}
                onMouseLeave={handleDropdownLeave}
              >
                <div
                  className={`flex items-center px-3 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 ${
                    isScrolled
                      ? 'text-slate-700 hover:text-purple-600 hover:bg-slate-100'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => handleDropdownClick('language')}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  <span>{getCurrentLanguageInfo().flag}</span>
                  <span className="ml-1">
                    {getCurrentLanguageInfo().name}
                  </span>
                  <ChevronDown
                    className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                      activeDropdown === 'language' ? 'rotate-180' : ''
                    }`}
                  />
                </div>

                {activeDropdown === 'language' && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-slate-200/20 py-2 z-[60]">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setActiveDropdown(null);
                        }}
                        className={`w-full flex items-center px-4 py-2 text-left text-slate-700 hover:bg-purple-50 hover:text-purple-600 transition-colors duration-200 ${
                          language === lang.code
                            ? 'bg-purple-50 text-purple-600'
                            : ''
                        }`}
                      >
                        <span className="text-lg mr-3">{lang.flag}</span>
                        <span className="text-sm">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile menu button + Language Selector */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Language Selector */}
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  if (activeDropdown === 'mobile-language') {
                    setActiveDropdown(null);
                  } else {
                    setActiveDropdown('mobile-language');
                  }
                }}
                className={`flex items-center px-2 py-2 rounded-full transition-colors duration-200 ${
                  isScrolled
                    ? 'text-slate-700 hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <span className="text-lg">
                  {getCurrentLanguageInfo().flag}
                </span>
              </button>

              {activeDropdown === 'mobile-language' && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white/95 backdrop-blur-lg rounded-xl shadow-xl border border-slate-200/20 py-2 z-[70]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setActiveDropdown(null);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-left text-sm transition-colors duration-200 ${
                        language === lang.code
                          ? 'bg-purple-50 text-purple-600'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-full transition-colors duration-200 ${
                isScrolled
                  ? 'text-slate-700 hover:bg-slate-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200/20 shadow-xl">
            <div className="px-6 py-4 space-y-2">
              {navigation.map((item) => (
                <div key={item.id}>
                  {item.dropdown ? (
                    <div>
                      <Link
                        to={item.href}
                        className={`flex items-center justify-between px-4 py-3 text-sm font-medium rounded-xl transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'bg-purple-50 text-purple-600'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span>{t(item.labelKey)}</span>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newDropdown =
                              activeDropdown === `mobile-${item.id}`
                                ? null
                                : `mobile-${item.id}`;
                            setActiveDropdown(newDropdown);
                          }}
                          className="p-1"
                        >
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-200 ${
                              activeDropdown === `mobile-${item.id}`
                                ? 'rotate-180'
                                : ''
                            }`}
                          />
                        </button>
                      </Link>

                      {activeDropdown === `mobile-${item.id}` && (
                        <div className="ml-4 mt-2 space-y-1">
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.id}
                              to={subItem.href}
                              className="block px-4 py-2 text-xs text-slate-600 hover:text-purple-600 transition-colors duration-200"
                              onClick={() => {
                                setIsMenuOpen(false);
                                setActiveDropdown(null);
                              }}
                            >
                              {t(subItem.labelKey)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block px-4 py-3 text-sm font-medium rounded-xl transition-colors duration-200 ${
                        isActive(item.href)
                          ? 'bg-purple-50 text-purple-600'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t(item.labelKey)}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
